import { Wheat, Soup, Droplet, Sprout, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { VALUE_PILLARS, PILLAR_IMAGES, LOGO_MARK } from "../../data/promill";

const ICONS = { wrap: Wheat, bowl: Soup, sauce: Droplet, leaf: Sprout };
const IMAGES = [PILLAR_IMAGES.wrap, PILLAR_IMAGES.bowl, PILLAR_IMAGES.sauce, PILLAR_IMAGES.addons];

// Decorative leaf SVG
const Leaf = ({ className, rotate = 0 }) => (
    <svg viewBox="0 0 64 64" className={className} style={{ transform: `rotate(${rotate}deg)` }}>
        <path
            d="M32 4 C18 12 10 24 12 38 C14 52 26 60 38 56 C30 50 24 40 22 30 C28 38 38 42 50 38 C58 26 50 12 32 4 Z"
            fill="currentColor"
        />
    </svg>
);

export const ValuePillars = () => (
    <section
        id="food-promise"
        data-testid="value-pillars-section"
        className="relative bg-pm-cream pt-6 md:pt-10 pb-16 md:pb-24 overflow-hidden"
    >
        {/* Decorative leaves */}
        <Leaf className="absolute top-12 left-0 w-32 h-32 text-pm-greenBright/15 -translate-x-12" rotate={-30} />
        <Leaf className="absolute bottom-16 right-0 w-40 h-40 text-pm-greenBright/15 translate-x-14" rotate={140} />
        <Leaf className="absolute top-1/2 right-[15%] w-10 h-10 text-pm-yellow/40 hidden lg:block" rotate={20} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
                {/* Left: Heading + CTA */}
                <div className="lg:col-span-4 lg:sticky lg:top-28">
                    <div className="inline-flex items-center gap-2 mb-5">
                        <img src={LOGO_MARK} alt="" className="h-6 w-6" />
                        <span className="text-pm-green font-bold tracking-[0.22em] uppercase text-[11px] md:text-xs">
                            The ProMill Promise
                        </span>
                    </div>
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-[-0.02em] text-pm-black leading-[1.02]">
                        Fast food, <br />
                        <span className="text-pm-yellow">rebuilt cleaner.</span>
                    </h2>
                    <p className="mt-5 text-pm-black/65 leading-relaxed max-w-sm">
                        Protein-first meals, millet-powered bases, cleaner sauces, and ingredients
                        you can feel good about.
                    </p>
                    <Link
                        to="/menu"
                        data-testid="pillars-explore-cta"
                        className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-pm-yellow text-pm-black font-bold text-sm hover:bg-pm-yellowBright transition-colors shadow-sm"
                    >
                        Explore Menu
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                {/* Right: 4 rich cards */}
                <div className="lg:col-span-8 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                    {VALUE_PILLARS.map((p, idx) => {
                        const Icon = ICONS[p.icon] || Sprout;
                        const img = IMAGES[idx];
                        return (
                            <div
                                key={p.title}
                                data-testid={`pillar-card-${idx}`}
                                className="group bg-white rounded-3xl p-5 md:p-6 flex flex-col items-center text-center card-lift border border-pm-black/5 hover:border-pm-yellow hover:shadow-[0_18px_40px_rgb(0,0,0,0.06)]"
                            >
                                {/* Icon */}
                                <div className="h-14 w-14 md:h-16 md:w-16 rounded-full bg-pm-yellow/20 flex items-center justify-center mb-4 group-hover:bg-pm-yellow/40 transition-colors">
                                    <Icon className="w-7 h-7 md:w-8 md:h-8 text-pm-green" strokeWidth={1.7} />
                                </div>
                                <h3 className="text-base md:text-lg font-extrabold text-pm-black text-center">{p.title}</h3>
                                <p className="mt-1.5 text-xs md:text-sm text-pm-black/60 text-center leading-relaxed min-h-[40px]">
                                    {p.desc}
                                </p>
                                {/* Food image */}
                                <div className="mt-4 -mx-2 md:-mx-3 relative w-[calc(100%+1rem)] md:w-[calc(100%+1.5rem)]">
                                    <div className="aspect-square rounded-2xl overflow-hidden bg-white">
                                        <img
                                            src={img}
                                            alt={p.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    </section>
);
